import { useQuery } from '@tanstack/react-query'
import { handleGetRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { useFilters } from '../../hooks/useFilters'
import { FILTER_TYPES } from '../../constants/filters'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { formatTime } from '../../utils/date.util'
import { VERIFICATION_STATUSES } from '../../constants/verificationStatuses'
import ViewButton from '../../components/common/ViewButton'
import StatusPill from '../../components/Verification/StatusPill'
import DisplayShopName from '../../components/common/DisplayShopName'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'

const ShopVerifications = () => {
  const { renderFilters, filters, renderPagination } = useFilters({
    [FILTER_TYPES.shopVerificationStatus]: VERIFICATION_STATUSES.submitted.value,
  })

  const { data: shopVerificationsData, isLoading } = useQuery({
    queryKey: QueryKeys.adminListShopVerifications(filters),
    queryFn: () => handleGetRequest(`/admin/shop-verifications`, filters),
  })

  const shopVerifications = shopVerificationsData?.data || []

  return (
    <>
      <div className="w-full flex mb-2">{renderFilters([FILTER_TYPES.search])}</div>
      <div className="flex gap-6 mb-8">
        {renderFilters([
          FILTER_TYPES.shopVerificationStatus,
          FILTER_TYPES.userType,
          FILTER_TYPES.date,
          FILTER_TYPES.sort,
        ])}
      </div>

      <div className="border border-gray-300 rounded-md overflow-hidden bg-background">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-secondary-100 [&>th]:px-6 [&>th]:py-4 [&>th]:font-medium [&>th]:text-black [&>th]:text-base">
              <th>Shop</th>
              <th>All Documents</th>
              <th>Status</th>
              <th>Reviewer</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="5" className="text-center text-gray-400 py-8">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : shopVerifications.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center text-gray-400 py-8">
                  No Shop Verifications Found
                </td>
              </tr>
            ) : (
              shopVerifications.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-gray-200 last:border-b-0 [&>td]:px-6 [&>td]:py-4"
                >
                  <td>
                    <DisplayShopName
                      shop={{
                        name: row.shopName,
                        isPremium: row.isShopPremium,
                        isVerified: row.isShopApproved,
                      }}
                    />
                  </td>
                  <td>
                    {row.hasUploadedAllRequiredDocuments ? (
                      <span className="text-success-600">
                        <CheckIcon className="w-4 h-4 inline-block" />
                      </span>
                    ) : (
                      <span className="text-error-700">
                        <XMarkIcon className="w-4 h-4 inline-block" />
                      </span>
                    )}
                  </td>
                  <td>
                    <StatusPill status={row.status} />
                  </td>
                  <td>{row.reviewerName || '--'}</td>
                  <td>{formatTime(row.createdAt)}</td>
                  <td className="px-6 py-4">
                    <ViewButton redirectPath={`/admin/shop-verifications/${row.id}`} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {renderPagination(shopVerificationsData?.meta)}
      </div>
    </>
  )
}

export default ShopVerifications
